import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ThemeProvider, Typography, useTheme, useMediaQuery, Box } from "@mui/material";
import { Button, Input, Tag, Space } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { useParams } from "react-router-dom";

import Sidebars from "../../Components/Sidebar/sidebar";
import DataTable from "../../Components/DataTable";

import appColors from "../../config/appColors";
import { postRequest } from "../../Api";
import { apiRoutes } from "../../Api/apiRoutes";
import { setLoader } from "../../Redux/Reducers/gernalSlice";
import { getDateString } from "../../config/functions";

const VehicleRentalHistory = () => {
    const { id } = useParams();
    const theme = useTheme();
    const dispatch = useDispatch();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const user = useSelector((state) => state.authReducer.user);

    const [overallSummary, setOverallSummary] = useState({});
    const [filterInfo, setFilterInfo] = useState({});
    const [activeAgreement, setActiveAgreement] = useState(null);
    const [history, setHistory] = useState([]);

    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    /* ===================== API ===================== */
    const getRentalHistory = async () => {
        const onSuccess = (res) => {
            setOverallSummary(res.overall_summary || {});
            setFilterInfo(res.filter || {});
            setActiveAgreement(res.current_active_agreement || null);
            setHistory(res.history || []);
            dispatch(setLoader(false));
        };

        const onError = () => {
            dispatch(setLoader(false));
        };

        dispatch(setLoader(true));
        await postRequest(
            {
                token: user?.token,
                veh_id: id,
                start_date: startDate || undefined,
                end_date: endDate || undefined,
            },
            apiRoutes.getVehicleRentalHistory,
            onSuccess,
            onError
        );
    };

    useEffect(() => {
        getRentalHistory();
    }, []);

    /* ===================== TABLE COLUMNS ===================== */
    const historyColumns = [
        { title: "Agreement ID", dataIndex: "agreement_id", key: "agreement_id" },
        { title: "Customer", dataIndex: "customer_name", key: "customer_name" },
        { title: "Phone", dataIndex: "customer_phone", key: "customer_phone" },
        { title: "Email", dataIndex: "customer_email", key: "customer_email" },
        {
            title: "Start Date",
            render: (_, r) => getDateString(r.start_date),
        },
        {
            title: "End Date",
            render: (_, r) => (r.end_date ? getDateString(r.end_date) : "-"),
        },
        {
            title: "Status",
            render: (_, r) => (
                <Tag color={r.status === "active" ? "green" : "default"} style={{ fontWeight: 600 }}>
                    {r.status?.toUpperCase()}
                </Tag>
            ),
        },
        { title: "Total Rent", dataIndex: "total_rent_generated", render: (v) => v ?? "0" },
        { title: "Paid", dataIndex: "total_paid", render: (v) => v ?? "0" },
        { title: "Remaining", dataIndex: "remaining", render: (v) => v ?? "0" },
        {
            title: "Agreement",
            render: (_, r) =>
                r.agreement_path ? (
                    <Button
                        type="link"
                        size="small"
                        icon={<DownloadOutlined />}
                        href={r.agreement_path}
                        target="_blank"
                    >
                        Download PDF
                    </Button>
                ) : "-",
        },
    ];

    /* ===================== STYLES (Matching VehicleDetails) ===================== */
    const infoContainer = {
        width: 250,
        borderRadius: 10,
        backgroundColor: appColors.white,
        padding: 5,
        paddingBottom: 20,
        marginTop: 10,
        marginRight: 10,
    };

    const infoHeadingContainer = {
        width: "100%",
        backgroundColor: appColors.primaryLight,
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10,
        height: 40,
        alignItems: "center",
        justifyContent: "center",
        display: "flex",
    };

    const infoHeading = {
        fontSize: 12,
        color: appColors.white,
        fontFamily: "Poppins",
        fontWeight: 600,
    };

    const infoValueContainer = {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        marginTop: 15,
    };

    const infoValue = {
        fontSize: 14,
        color: appColors.black,
        fontFamily: "Poppins",
        fontWeight: 600,
    };

    const sectionStyle = {
        backgroundColor: appColors.white,
        borderRadius: 10,
        padding: 15,
        marginTop: 20,
    };

    const primaryHeader = {
        width: "100%",
        height: 45,
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10,
        backgroundColor: appColors.primary,
        display: "flex",
        alignItems: "center",
        paddingLeft: 15,
        marginBottom: 15,
    };

    /* ===================== UI ===================== */
    return (
        <ThemeProvider theme={theme}>
            <div style={{ display: "flex" }}>
                <Sidebars />

                <div style={{ marginLeft: 20, width: "75%" }}>
                    {/* Header */}
                    <span className={`headertext ${isSmallScreen ? "small-screen" : ""}`}>
                        Rental History
                    </span>

                    {/* Filter Row */}
                    <div style={{ display: "flex", alignItems: "center", marginTop: 10, gap: 10 }}>
                        <Input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            style={{ width: 150 }}
                        />
                        <Input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            style={{ width: 150 }}
                        />
                        <Button type="primary" onClick={getRentalHistory}>
                            Apply Filter
                        </Button>
                    </div>

                    {/* Period Info */}
                    <div
                        style={{
                            backgroundColor: appColors.white,
                            padding: "10px 15px",
                            borderRadius: 10,
                            marginTop: 10,
                            fontSize: 13,
                            color: appColors.greyText,
                            fontFamily: "Poppins",
                        }}
                    >
                        Period: {filterInfo.start_date} → {filterInfo.end_date} ({filterInfo.note})
                    </div>

                    {/* Overall Summary Stats */}
                    <div style={{ display: "flex", flexWrap: "wrap", marginTop: 10 }}>
                        <div style={infoContainer}>
                            <div style={infoHeadingContainer}>
                                <Typography sx={infoHeading}>Total Agreements</Typography>
                            </div>
                            <div style={infoValueContainer}>
                                <Typography sx={infoValue}>{overallSummary.total_agreements ?? "-"}</Typography>
                            </div>
                        </div>

                        <div style={infoContainer}>
                            <div style={infoHeadingContainer}>
                                <Typography sx={infoHeading}>Total Invoice Amount</Typography>
                            </div>
                            <div style={infoValueContainer}>
                                <Typography sx={infoValue}>{overallSummary.total_invoice_amount ?? "-"}</Typography>
                            </div>
                        </div>

                        <div style={infoContainer}>
                            <div style={infoHeadingContainer}>
                                <Typography sx={infoHeading}>Total Paid Amount</Typography>
                            </div>
                            <div style={infoValueContainer}>
                                <Typography sx={infoValue}>{overallSummary.total_paid_amount ?? "-"}</Typography>
                            </div>
                        </div>

                        <div style={infoContainer}>
                            <div style={infoHeadingContainer}>
                                <Typography sx={infoHeading}>Total Remaining</Typography>
                            </div>
                            <div style={infoValueContainer}>
                                <Typography sx={infoValue}>{overallSummary.total_remaining ?? "-"}</Typography>
                            </div>
                        </div>

                        <div style={infoContainer}>
                            <div style={infoHeadingContainer}>
                                <Typography sx={infoHeading}>Total Rented Persons</Typography>
                            </div>
                            <div style={infoValueContainer}>
                                <Typography sx={infoValue}>{overallSummary.total_rented_persons ?? "-"}</Typography>
                            </div>
                        </div>
                    </div>

                    {/* Current Active Agreement */}
                    {activeAgreement && (
                        <div style={sectionStyle}>
                            <div style={primaryHeader}>
                                <Typography style={{ color: appColors.white, fontSize: 16, fontWeight: 600 }}>
                                    Current Active Agreement
                                </Typography>
                            </div>

                            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                <Typography style={{ fontFamily: "Poppins", fontSize: 14 }}>
                                    <strong>Customer:</strong> {activeAgreement.customer_name}
                                </Typography>
                                <Typography style={{ fontFamily: "Poppins", fontSize: 13, color: appColors.greyText }}>
                                    <strong>Phone:</strong> {activeAgreement.customer_phone || "-"} |{" "}
                                    <strong>Email:</strong> {activeAgreement.customer_email || "-"}
                                </Typography>
                                <Typography style={{ fontFamily: "Poppins", fontSize: 13 }}>
                                    <strong>Period:</strong> {getDateString(activeAgreement.start_date)} →{" "}
                                    {getDateString(activeAgreement.end_date || activeAgreement.expected_end_date)}
                                </Typography>
                                <Typography style={{ fontFamily: "Poppins", fontSize: 13 }}>
                                    <strong>Status:</strong>{" "}
                                    <Tag color="green" style={{ fontWeight: 600 }}>ACTIVE</Tag>
                                </Typography>

                                {activeAgreement.agreement_path && (
                                    <Button
                                        type="primary"
                                        icon={<DownloadOutlined />}
                                        href={activeAgreement.agreement_path}
                                        target="_blank"
                                        style={{ width: "fit-content", marginTop: 10 }}
                                    >
                                        Download Agreement PDF
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Rental History Table */}
                    <div style={sectionStyle}>
                        <div style={primaryHeader}>
                            <Typography style={{ color: appColors.white, fontSize: 16, fontWeight: 600 }}>
                                Rental History ({history.length} records)
                            </Typography>
                        </div>

                        <div style={{ overflowX: "auto" }}>
                            <DataTable
                                columns={historyColumns}
                                data={history}
                                searchColoumns={["customer_name", "customer_phone", "agreement_id"]}
                                scroll={{ x: "max-content" }}
                                pagination={{ pageSize: 15 }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </ThemeProvider>
    );
};

export default VehicleRentalHistory;